import { logger } from "#webapp/src/util/logger.util";
import {
  DEFAULT_LAUNCH_DARKLY_FLAGS,
  LaunchDarklyMappingType,
  LaunchDarklyNameType,
} from "@clockwise/client-commons/src/util/launch-darkly-flags";
import { LDContext } from "launchdarkly-js-client-sdk";
import { useLDClient, useLDClientError } from "launchdarkly-react-client-sdk";
import { keys } from "lodash";
import React, { PropsWithChildren, createContext, useCallback, useContext, useEffect } from "react";
import { LaunchDarklyClient, Org, User, buildLaunchDarklyContext } from "./LaunchDarklyClient";

const getDefaultFlag = (key: LaunchDarklyNameType) => DEFAULT_LAUNCH_DARKLY_FLAGS[key];

type LaunchDarklyContext = ReturnType<typeof useLaunchDarklyState>;

const Context = createContext<LaunchDarklyContext | null>(null);

const emptyContext: LaunchDarklyContext = {
  isLDInitialized: false,
  allFlags: DEFAULT_LAUNCH_DARKLY_FLAGS,
  evaluateFlag: (key: LaunchDarklyNameType) => getDefaultFlag(key),
  loading: false,
  error: undefined,
};

const LD_ORG_UNKNOWN_KEY = "unknown_org";

const anonymousContext: LDContext = {
  kind: "multi",
  // Anonymous users must not have a `key` set to have an auto-generated stable key that persists
  // in local storage. See: https://docs.launchdarkly.com/home/contexts/anonymous-contexts?q=anonymous#storing-anonymous-keys-locally
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  user: {
    kind: "user",
    anonymous: true,
  },
  org: {
    key: LD_ORG_UNKNOWN_KEY,
    anonymous: true,
  },
};

export const useLaunchDarkly = (): LaunchDarklyContext => {
  const context = useContext(Context);

  if (!context) {
    console.error(
      "Tried to access launch darkly context but it is not available. Please ensure you are inside a LDFeatureProvider.",
    );
    return emptyContext;
  }

  return context;
};

export const useFeatureFlag = (
  flag: LaunchDarklyNameType,
): [value: boolean, loading: boolean, initalized: boolean] => {
  const { evaluateFlag, loading: launchDarklyLoading, isLDInitialized } = useLaunchDarkly();
  return [evaluateFlag(flag) as boolean, launchDarklyLoading, isLDInitialized];
};

// HACK: this is kinda a hack to get the maxTeamSizeOverride value in LD working. we should
// refactoer and make this code much more robust if we wanna use this more
export const useFeatureFlagNumber = (
  flag: LaunchDarklyNameType,
): [value: number, loading: boolean, initalized: boolean] => {
  const { evaluateFlag, loading: launchDarklyLoading, isLDInitialized } = useLaunchDarkly();
  return [evaluateFlag(flag) as number, launchDarklyLoading, isLDInitialized];
};

type LaunchDarklyFlagMap = Record<LaunchDarklyNameType, boolean | number>;
export const useAllFeatureFlags = (): { launchDarklyFlags: LaunchDarklyFlagMap } => {
  const allEvaluateFlags: LaunchDarklyFlagMap = {
    // We will populate this and override the default flags with the ones from the LD client
    ...DEFAULT_LAUNCH_DARKLY_FLAGS,
  };
  const { evaluateFlag } = useLaunchDarkly();

  keys(DEFAULT_LAUNCH_DARKLY_FLAGS).reduce((acc, flag: LaunchDarklyNameType) => {
    const evaluatedFlag = evaluateFlag(flag);

    acc[flag] = evaluatedFlag;

    return acc;
  }, allEvaluateFlags);

  return { launchDarklyFlags: allEvaluateFlags };
};

export type OverrideMapping = Partial<{ [k in LaunchDarklyNameType]: boolean | number }>;

const useLaunchDarklyState = (flagOverrides?: LaunchDarklyMappingType) => {
  const ldClient = useLDClient();
  const error = useLDClientError();

  useEffect(() => {
    if (error) {
      logger.error("A Launch Darkly error occurred", error);
    }
  }, [error]);

  // Returns a list of flags without evaluating them
  const allFlags = flagOverrides ?? ldClient?.allFlags() ?? DEFAULT_LAUNCH_DARKLY_FLAGS;

  const evaluateFlag = useCallback(
    (key: LaunchDarklyNameType): boolean | number => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return flagOverrides
        ? flagOverrides[key]
        : ldClient?.variation(key, getDefaultFlag(key)) ?? getDefaultFlag(key);
    },
    [ldClient, flagOverrides],
  );

  return {
    isLDInitialized: !!ldClient || !!flagOverrides,
    allFlags,
    evaluateFlag,
    loading: !ldClient && !flagOverrides,
    error,
  };
};

const LaunchDarklyProviderContext = ({
  children,
  user,
  flagOverrides,
  context,
}: PropsWithChildren<{
  user: User | null;
  flagOverrides?: LaunchDarklyMappingType;
  context?: LDContext;
}>) => {
  const ldClient = useLDClient();
  useEffect(() => {
    // When the user and context update, we need to call identify to tell LD to fetch the flags for the new userId
    if (ldClient && context && user) {
      ldClient.identify(context, undefined);
    }
  }, [context, user]);

  return (
    <LaunchDarklyStateProvider flagOverrides={flagOverrides}>{children}</LaunchDarklyStateProvider>
  );
};

export const LaunchDarklyProvider = ({
  children,
  org,
  user,
  loading,
  flagOverrides,
}: PropsWithChildren<{
  org: Org | null;
  user: User | null;
  loading: boolean;
  flagOverrides?: LaunchDarklyMappingType;
}>) => {
  const context = !loading
    ? !!user && !!org
      ? buildLaunchDarklyContext(user, org)
      : anonymousContext
    : undefined;

  // If overrides are provided, we can skip the LaunchDarklyClient entirely.
  if (flagOverrides) {
    return (
      <LaunchDarklyProviderContext flagOverrides={flagOverrides} user={user} context={context}>
        {children}
      </LaunchDarklyProviderContext>
    );
  }

  return (
    <LaunchDarklyClient context={context}>
      <LaunchDarklyProviderContext flagOverrides={flagOverrides} user={user} context={context}>
        {children}
      </LaunchDarklyProviderContext>
    </LaunchDarklyClient>
  );
};

const LaunchDarklyStateProvider = ({
  children,
  flagOverrides,
}: PropsWithChildren<{ flagOverrides?: LaunchDarklyMappingType }>) => {
  const value = useLaunchDarklyState(flagOverrides);
  return <Context.Provider value={value}>{children}</Context.Provider>;
};
